/**
 * <LiveReportHeaderSkeleton> — la forme de `<LiveReportHeader>` pendant un premier chargement.
 *
 * Posé par les squelettes des deux pages qui montrent l'en-tête (`ParentHomeSkeleton` pour
 * « Dernier bilan », `LiveReportsListSkeleton` pour l'onglet Rapports) : une puce de chapitre, la
 * capsule du tuteur et sa ligne — sur un niveau ou deux, par la même mesure et le même seuil que
 * l'en-tête —, l'anneau et son mot à droite. Il montre le cas courant : un chapitre, un score.
 *
 * Les tailles viennent de l'en-tête lui-même (`GAUGE_SIZE`, `AVATAR_SIZE`, `ONE_LINE_FROM`) : une
 * retouche de l'en-tête se voit ici sans recopie.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Skeleton } from "../../atoms/Skeleton"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { AVATAR_SIZE, GAUGE_SIZE, ONE_LINE_FROM } from "./LiveReportHeader"

/** Une puce de chapitre (`<ChapterBadge>`) : une ligne `text-sm`, 3 px de retrait, un filet. */
const CHAPTER_CHIP_HEIGHT = 23

export type LiveReportHeaderSkeletonProps = {
    size?: "sm" | "md"
}

export function LiveReportHeaderSkeleton(props: LiveReportHeaderSkeletonProps) {
    const { size = "md" } = props
    const { onLayout, atLeast: oneLine } = useWidthAtLeast(ONE_LINE_FROM)

    return (
        <Row gap="3" align="center">
            <Stack gap="2" style={styles.fill}>
                <Skeleton shape="block" width={190} height={CHAPTER_CHIP_HEIGHT} radius="sm" />
                <Row gap="2" align="center" style={styles.bow}>
                    <Skeleton shape="circle" size={AVATAR_SIZE} />
                    <View style={styles.fill} onLayout={onLayout}>
                        {oneLine ? (
                            <Skeleton textSize="text-md" width={290} />
                        ) : (
                            <>
                                <Skeleton textSize="text-md" width={150} />
                                <Skeleton textSize="text-sm" width={110} />
                            </>
                        )}
                    </View>
                </Row>
            </Stack>

            <Stack gap="1" align="center">
                <Skeleton shape="circle" size={GAUGE_SIZE[size]} />
                <Skeleton textSize={size === "sm" ? "text-xs" : "text-sm"} width={52} />
            </Stack>
        </Row>
    )
}

const styles = StyleSheet.create(() => ({
    fill: { flex: 1, flexShrink: 1 },
    /** Le nœud de la capsule pend sous la ligne, comme dans l'en-tête. */
    bow: { paddingBottom: 6 },
}))
